import * as React from "react";
import { ReactFlow, Background, BackgroundVariant, type Node, type Edge } from "@xyflow/react";
import "@xyflow/react/dist/style.css";
import nodeTypes from "./nodeTypes";
import DataEdge from "./edgeTypes/DataEdge";
import ExecEdge from "./edgeTypes/ExecEdge";
import "@flows/canvas/colors.css";
import type { StoryMeta } from "@features/devtools/registry/types";
import type { NodeTemplate } from "@features/flows/palette/registry";
import { inferTypes, applyInferredTypesToNodes } from "@features/flows/lib/type-infer";

type Props = {
  asConst?: boolean;
  keyPath?: string; // property to project
  applyInference?: boolean;
};

function buildTemplates(): Map<string, NodeTemplate> {
  const map = new Map<string, NodeTemplate>();
  // Primitive: object literal → out:value via literalType when asConst is true
  map.set("primitives.object", {
    key: "primitives.object",
    title: "Primitive • Object",
    category: "Primitives",
    ports: {
      exec: { in: [{ id: "in", type: "any" }], out: [{ id: "next", type: "any" }] },
      data: { in: [], out: [{ id: "value", type: "object" }] },
    },
    // minimally model attrs; UI is not rendered in this story
    attrsSchema: { type: "object", properties: { value: { type: "object" }, asConst: { type: "boolean" } } },
    pkg: "primitives",
    typeLinks: [
      { from: { attr: "value", op: "literalType" }, to: { io: "out", id: "value" } },
    ],
  } as any);

  // Object.get demo: project property from shape using node attr 'key'
  map.set("core.object.get", {
    key: "core.object.get",
    title: "Object • Get",
    category: "Object",
    ports: {
      exec: { in: [{ id: "in", type: "any" }], out: [{ id: "next", type: "any" }] },
      data: { in: [{ id: "object", type: "object" }], out: [{ id: "value", type: "any" }] },
    },
    attrsSchema: { type: "object", properties: { key: { type: "string" } } },
    pkg: "core",
    typeLinks: [
      { from: { io: "in", id: "object", op: { name: "shapeProp", keyAttr: "key" } }, to: { io: "out", id: "value" } },
    ],
  } as any);
  return map;
}

function LiteralsAsConstPreview({ asConst = true, keyPath = "name", applyInference = true }: Props) {
  const templates = React.useMemo(buildTemplates, []);
  const baseNodes: Node[] = [
    {
      id: "obj",
      type: "default",
      position: { x: 80, y: 140 },
      data: {
        label: "Primitive • Object",
        templateKey: "primitives.object",
        attrs: { value: { name: "Marc", age: 12 }, asConst },
        pins: {
          exec: { in: [{ id: "in" }], out: [{ id: "next" }] },
          data: { in: [], out: [{ id: "value", type: "object" }] },
        },
      },
    },
    {
      id: "get",
      type: "default",
      position: { x: 420, y: 140 },
      data: {
        label: "Object • Get (.name)",
        templateKey: "core.object.get",
        attrs: { key: keyPath },
        pins: {
          exec: { in: [{ id: "in" }], out: [{ id: "next" }] },
          data: { in: [{ id: "object", type: "object" }], out: [{ id: "value", type: "any" }] },
        },
      },
    },
  ];
  const baseEdges: Edge[] = [
    {
      id: "e-obj",
      type: "data",
      source: "obj",
      target: "get",
      sourceHandle: "data:out:value",
      targetHandle: "data:in:object",
      data: { channel: "data", rawSourceHandle: "value", rawTargetHandle: "object" },
    } as any,
  ];
  const edgeTypes = React.useMemo(() => ({ data: DataEdge, exec: ExecEdge }), []);
  const [nodes, setNodes] = React.useState<Node[]>(baseNodes);
  const [edges, setEdges] = React.useState<Edge[]>(baseEdges);

  React.useEffect(() => {
    const n0 = [
      { ...baseNodes[0], data: { ...(baseNodes[0].data as any), attrs: { value: { name: "Marc", age: 12 }, asConst } } } as Node,
      { ...baseNodes[1], data: { ...(baseNodes[1].data as any), attrs: { key: keyPath } } } as Node,
    ];
    if (!applyInference) { setNodes(n0); setEdges(baseEdges); return; }
    try {
      const inferred = inferTypes(n0 as any, baseEdges as any, templates);
      const nextNodes = applyInferredTypesToNodes(n0 as any, inferred) as any as Node[];
      setNodes(nextNodes);
      setEdges(baseEdges);
    } catch {
      setNodes(n0);
      setEdges(baseEdges);
    }
  }, [applyInference, asConst, keyPath, templates]);

  return (
    <div className="h-[560px] border rounded">
      <ReactFlow<Node, Edge>
        nodes={nodes}
        edges={edges}
        nodeTypes={nodeTypes}
        edgeTypes={edgeTypes}
        fitView
        panOnScroll
        panOnDrag
        zoomOnScroll
      >
        <Background variant={BackgroundVariant.Dots} gap={24} size={1} />
      </ReactFlow>
    </div>
  );
}

const meta: StoryMeta<Props> = {
  id: "flows/LiteralsAsConst",
  title: "Literals (as const)",
  group: "Flows/Typing",
  component: LiteralsAsConstPreview as any,
  description: "Object literal with asConst refines out pin to shape; object.get picks a property type.",
  defaultProps: { asConst: true, keyPath: "name", applyInference: true },
  controls: {
    asConst: { type: "boolean", label: "As const" },
    keyPath: { type: "text", label: "Key" },
    applyInference: { type: "boolean", label: "Apply Inference" },
  },
  variants: [
    { name: "As const (name:string)", props: { asConst: true, keyPath: "name" } },
    { name: "As const (age:number)", props: { asConst: true, keyPath: "age" } },
    { name: "Toggle off (no refine)", props: { asConst: false } },
  ],
};

export default meta;

